/**
 * DashboardPage Component
 * Displays KPI summary cards and a filterable list of all BRR sessions.
 * Data sourced from the session store brr_session table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  RefreshCw,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  BarChart3,
  Loader2,
  Download
} from 'lucide-react';
import { SessionAuditClient, SessionStatus, BRRApiClient, AnalyticsBreakdownEntry } from '../services';
import { useDashboardData, StatusFilter, TimeWindow, BatchTypeFilter, PriorityFilter, DelayReasonFilter, computeReleaseDelay } from '../hooks/useDashboardData';
import { StatusBadge } from '../components/StatusBadge';
import { Batch, Priority, UserRole, DELAY_REASON_LABELS } from '../services/types';
import { isAtLeast } from '../utils/userRole';

interface DashboardPageProps {
  userEmail: string;
  userRole?: UserRole;
  sessionClient: SessionAuditClient;
  apiClient?: BRRApiClient;
  onSelectBatch: (batch: Batch) => void;
  isActive?: boolean;
}

const KPICard = ({ icon: Icon, label, value, color, bgColor }: {
  icon: React.ComponentType<any>;
  label: string;
  value: string | number;
  color: string;
  bgColor: string;
}) => (
  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded ${bgColor}`}>
        <Icon size={20} className={color} />
      </div>
    </div>
    <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

const AnalyticsTable = ({ title, data }: { title: string; data: AnalyticsBreakdownEntry[] }) => (
  <div>
    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</div>
    {data.length === 0 ? (
      <div className="text-xs text-gray-400">No data</div>
    ) : (
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-1 font-medium text-gray-500">Label</th>
            <th className="text-right py-1 font-medium text-gray-500">Avg Time</th>
            <th className="text-right py-1 font-medium text-gray-500">Count</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.label} className="border-b border-gray-50">
              <td className="py-1 text-gray-700 truncate max-w-[140px]" title={row.label}>
                {row.label.charAt(0).toUpperCase() + row.label.slice(1)}
              </td>
              <td className="py-1 text-right text-gray-600">{row.avgDays.toFixed(1)}d</td>
              <td className="py-1 text-right text-gray-600">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'submitted', label: 'Pending QP' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
];

const TIME_WINDOW_OPTIONS: { value: TimeWindow; label: string }[] = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '1y', label: 'Last year' },
  { value: 'all', label: 'All time' },
];

const BATCH_TYPE_OPTIONS: { value: BatchTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Batch Types' },
  { value: 'direct', label: 'Direct' },
  { value: 'indirect', label: 'Indirect' },
];

const PRIORITY_OPTIONS: { value: PriorityFilter; label: string }[] = [
  { value: 'all', label: 'All Priorities' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const DELAY_REASON_OPTIONS: { value: DelayReasonFilter; label: string }[] = [
  { value: 'all', label: 'All Delays' },
  { value: 'delayed', label: 'Delayed Only' },
  { value: 'no_inspection_lot', label: 'No Inspection Lot' },
  { value: 'missing_cmo_documents', label: 'Missing CMO Docs' },
  { value: 'missing_serialisation', label: 'Missing Serialisation' },
  { value: 'other', label: 'Other' },
];

type SortField = 'batchid' | 'batchtype' | 'productname' | 'status' | 'delayreason' | 'expectedreleasedate' | 'priority' | 'useremail' | 'startedat' | 'completedat';
type SortDir = 'asc' | 'desc';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50] as const;

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function getProductName(session: any, columns: any): string {
  const direct = session[columns.productname];
  if (direct) return direct;
  // Fallback: parse from details JSON for sessions created before the dedicated column existed
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.productName) return parsed.productName;
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function getInspectionLotId(session: any, columns: any): string | null {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.inspectionLotId) return String(parsed.inspectionLotId);
    }
  } catch { /* ignore parse errors */ }
  return null;
}

function getBatchType(session: any, columns: any): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.batchType) return parsed.batchType;
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function getPriority(session: any, columns: any): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.priority) {
        return parsed.priority === 'normal' ? 'medium' : parsed.priority;
      }
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function getDelayReason(session: any, columns: any): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.delayReason) return parsed.delayReason;
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function getDelayDetails(session: any, columns: any): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.delayDetails) return parsed.delayDetails;
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function getDelayActions(session: any, columns: any): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.delayActions) return parsed.delayActions;
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function getDelayCmoName(session: any, columns: any): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.delayCmoName) return parsed.delayCmoName;
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function getExpectedReleaseDate(session: any, columns: any): string {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.expectedReleaseDate) return parsed.expectedReleaseDate;
    }
  } catch { /* ignore parse errors */ }
  return '';
}

function formatDateShort(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value + 'T00:00:00');
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatProcessingTime(startedAt: string | null | undefined, completedAt: string | null | undefined, inProgressAt?: string | null, docCompletenessAt?: string | null): string {
  const processStart = inProgressAt || docCompletenessAt || startedAt;
  if (!processStart || !completedAt) return '-';
  const ms = new Date(completedAt).getTime() - new Date(processStart).getTime();
  if (ms <= 0 || isNaN(ms)) return '-';
  const hours = ms / (1000 * 60 * 60);
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function getInProgressAt(session: any, columns: any): string | null {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.inProgressAt) return parsed.inProgressAt;
    }
  } catch { /* ignore parse errors */ }
  return null;
}

function getDocCompletenessAt(session: any, columns: any): string | null {
  try {
    const details = session[columns.details];
    if (details) {
      const parsed = JSON.parse(details);
      if (parsed?.docCompletenessAt) return parsed.docCompletenessAt;
    }
  } catch { /* ignore parse errors */ }
  return null;
}

function formatReleaseDelay(hours: number): string {
  if (hours < 0) return 'Pre-released';
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

const DELAY_STYLES = {
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
} as const;

function exportDashboardToExcel(
  sessions: any[],
  columns: any,
  releaseDates: Record<string, any>,
  userRole: UserRole = 'viewer'
): void {
  const isAdminExport = userRole === 'admin';
  const isSupervisorExport = userRole === 'admin' || userRole === 'supervisor';
  const headers = [
    'Batch ID', 'Batch Type', 'Product', 'Status', 'Delay Reason', 'Expected Release',
    ...(isSupervisorExport ? ['Priority'] : []),
    ...(isSupervisorExport ? ['Reviewer'] : []),
    ...(isSupervisorExport ? ['Started On'] : []),
    'Completed On',
    ...(isAdminExport ? ['CMO Name', 'Delay Details', 'Actions Being Taken', 'Time', 'QP Release Time'] : []),
  ];

  const rows = sessions.map((session) => {
    const lotId = getInspectionLotId(session, columns);
    const releaseInfo = lotId ? releaseDates[lotId] : null;
    const delay = computeReleaseDelay(session[columns.submittedat], releaseInfo);
    const sessionStatus = session[columns.status];
    const delayText = delay.status === 'pre-released' ? 'Pre-released' : delay.status === 'rejected' ? 'Rejected' : delay.category && delay.hours !== null ? formatReleaseDelay(delay.hours) : (sessionStatus === SessionStatus.Submitted || sessionStatus === SessionStatus.Completed || sessionStatus === SessionStatus.Rejected ? 'Pending' : '');

    const bt = getBatchType(session, columns);
    const pri = getPriority(session, columns);
    const dr = getDelayReason(session, columns);
    const expectedRelease = formatDateShort(getExpectedReleaseDate(session, columns));
    return [
      session[columns.batchid] || '',
      bt ? bt.charAt(0).toUpperCase() + bt.slice(1) : '',
      getProductName(session, columns) || '',
      session[columns.status] || '',
      dr ? (DELAY_REASON_LABELS[dr] || dr) : '',
      expectedRelease !== '-' ? expectedRelease : '',
      ...(isSupervisorExport ? [pri ? pri.charAt(0).toUpperCase() + pri.slice(1) : ''] : []),
      ...(isSupervisorExport ? [session[columns.useremail] || ''] : []),
      ...(isSupervisorExport ? [formatDate(session[columns.startedat])] : []),
      formatDate(session[columns.completedat]),
      ...(isAdminExport ? [
        getDelayCmoName(session, columns),
        getDelayDetails(session, columns),
        getDelayActions(session, columns),
        formatProcessingTime(session[columns.startedat], session[columns.completedat], getInProgressAt(session, columns), getDocCompletenessAt(session, columns)),
        delayText,
      ] : []),
    ];
  });

  const escapeHtml = (str: string) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const exportDate = new Date().toISOString().split('T')[0];

  const tableHtml = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"></head>
      <body>
        <table style="border-collapse:collapse;width:100%;">
          <tr>
            <td colspan="${headers.length}" style="border:none;padding:8px 6px 2px 6px;">
              <span style="font-size:16pt;font-weight:bold;color:#008244;">Cheplapharm Arzneimittel GmbH</span>
            </td>
          </tr>
          <tr>
            <td colspan="${headers.length}" style="border:none;padding:0 6px 2px 6px;">
              <span style="font-size:10pt;color:#333;">Batch Record Review &mdash; Dashboard Export</span>
            </td>
          </tr>
          <tr>
            <td colspan="${headers.length}" style="border:none;padding:0 6px 8px 6px;">
              <span style="font-size:9pt;color:#666;">Date: ${exportDate} &nbsp;&nbsp;|&nbsp;&nbsp; Records: ${rows.length}</span>
            </td>
          </tr>
          <tr>
            <td colspan="${headers.length}" style="border:none;border-bottom:2px solid #008244;height:2px;"></td>
          </tr>
          <tr>
            <td colspan="${headers.length}" style="border:none;height:6px;"></td>
          </tr>
        </table>
        <table border="1" style="border-collapse:collapse;width:100%;">
          <thead>
            <tr>${headers.map((h) => `<th style="background-color:#008244;color:#fff;font-weight:bold;padding:6px;">${escapeHtml(h)}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${rows.map((row, i) => `<tr>${row.map((cell) => `<td style="padding:4px;${i % 2 === 1 ? 'background-color:#F2F2F2;' : ''}">${escapeHtml(String(cell))}</td>`).join('')}</tr>`).join('\n')}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const blob = new Blob([tableHtml], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `BRR_Dashboard_${new Date().toISOString().split('T')[0]}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const DashboardPageComponent: React.FC<DashboardPageProps> = ({
  userEmail,
  userRole = 'viewer',
  sessionClient,
  apiClient,
  onSelectBatch,
  isActive
}) => {
  const isAdmin = userRole === 'admin';
  const isSupervisorOrAbove = isAtLeast(userRole, 'supervisor');
  const {
    stats,
    analytics,
    filteredSessions,
    filters,
    setFilters,
    loading,
    error,
    isTruncated,
    refresh,
    releaseDates,
    distinctUsers,
    distinctCmos,
    distinctProducts,
  } = useDashboardData({ sessionClient, apiClient, userEmail, userRole });

  const columns = sessionClient.columns;

  const [sortField, setSortField] = useState<SortField>('startedat');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Admin priority overrides: sessionId → priority (optimistic updates)
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [priorityOverrides, setPriorityOverrides] = useState<Record<string, Priority>>({});
  const [priorityUpdating, setPriorityUpdating] = useState<Record<string, boolean>>({});

  const handlePriorityChange = useCallback(async (sessionId: string, batchId: string, newPriority: Priority) => {
    if (!apiClient) return;
    setPriorityUpdating((prev) => ({ ...prev, [sessionId]: true }));
    setPriorityOverrides((prev) => ({ ...prev, [sessionId]: newPriority }));
    try {
      await apiClient.updateReviewPriority({
        batchId,
        sessionId,
        priority: newPriority,
        isAdmin: isAdmin,
      });
    } catch (err) {
      setPriorityOverrides((prev) => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
      console.error('Failed to update priority:', err);
    } finally {
      setPriorityUpdating((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, [apiClient, userEmail]);

  // Reset to first page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedSessions = [...filteredSessions].sort((a, b) => {
    let aVal: any, bVal: any;
    if (sortField === 'productname') {
      aVal = getProductName(a, columns);
      bVal = getProductName(b, columns);
    } else if (sortField === 'batchtype') {
      aVal = getBatchType(a, columns);
      bVal = getBatchType(b, columns);
    } else if (sortField === 'delayreason') {
      aVal = getDelayReason(a, columns);
      bVal = getDelayReason(b, columns);
    } else if (sortField === 'priority') {
      aVal = getPriority(a, columns);
      bVal = getPriority(b, columns);
    } else if (sortField === 'expectedreleasedate') {
      aVal = getExpectedReleaseDate(a, columns);
      bVal = getExpectedReleaseDate(b, columns);
    } else {
      const colKey = columns[sortField as keyof typeof columns];
      aVal = a[colKey] ?? '';
      bVal = b[colKey] ?? '';
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sortedSessions.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedSessions = sortedSessions.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage
  );

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronDown size={14} className="inline ml-1 rotate-180" />
      : <ChevronDown size={14} className="inline ml-1" />;
  };

  const avgTimeDisplay = stats.avgProcessingTimeDays > 0
    ? `${stats.avgProcessingTimeDays.toFixed(1)}d`
    : '-';

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">BRR Dashboard</h1>
          <p className="text-sm text-gray-500">Batch Record Review Overview</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportDashboardToExcel(sortedSessions, columns, releaseDates, userRole)}
            disabled={loading || sortedSessions.length === 0}
            className="px-3 py-2 bg-white border border-gray-200 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="px-3 py-2 bg-white border border-gray-200 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div className={`grid gap-4 ${isAdmin ? 'grid-cols-4' : isSupervisorOrAbove ? 'grid-cols-3' : 'grid-cols-1'}`}>
        <KPICard
          icon={Package}
          label="Batches in Pipeline"
          value={loading ? '-' : stats.totalInPipeline}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        {isAdmin && (
          <KPICard
            icon={Clock}
            label="Avg. Processing Time"
            value={loading ? '-' : avgTimeDisplay}
            color="text-green-600"
            bgColor="bg-green-100"
          />
        )}
        {isSupervisorOrAbove && (
          <KPICard
            icon={CheckCircle}
            label="Completed Reviews"
            value={loading ? '-' : stats.completedCount}
            color="text-green-600"
            bgColor="bg-green-100"
          />
        )}
        {isSupervisorOrAbove && (
          <KPICard
            icon={AlertTriangle}
            label="Pending QP Review"
            value={loading ? '-' : stats.pendingActions}
            color="text-amber-600"
            bgColor="bg-amber-100"
          />
        )}
      </div>

      {/* Analytics Section (supervisor+) */}
      {isSupervisorOrAbove && analytics && !loading && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <button
            onClick={() => setAnalyticsOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <BarChart3 size={16} className="text-indigo-500" />
              Analytics Breakdown
            </span>
            <ChevronDown size={16} className={`transition-transform ${analyticsOpen ? 'rotate-180' : ''}`} />
          </button>
          {analyticsOpen && (
            <div className={`px-4 pb-4 grid ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'} gap-4 border-t border-gray-100 pt-3`}>
              {isAdmin && <AnalyticsTable title="By Reviewer" data={analytics.avgTimeByReviewer} />}
              <AnalyticsTable title="By Priority" data={analytics.avgTimeByPriority} />
              <AnalyticsTable title="By Batch Type" data={analytics.avgTimeByBatchType} />
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-gray-200 p-3">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ status: e.target.value as StatusFilter })}
          className="border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
        >
          {STATUS_OPTIONS
            .filter((opt) => isSupervisorOrAbove || opt.value !== 'submitted')
            .map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
        <select
          value={filters.timeWindow}
          onChange={(e) => setFilters({ timeWindow: e.target.value as TimeWindow })}
          className="border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
        >
          {TIME_WINDOW_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={filters.batchType}
          onChange={(e) => setFilters({ batchType: e.target.value as BatchTypeFilter })}
          className="border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
        >
          {BATCH_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {isSupervisorOrAbove && (
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ priority: e.target.value as PriorityFilter })}
            className="border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
          >
            {PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
        <select
          value={filters.delayReason}
          onChange={(e) => setFilters({ delayReason: e.target.value as DelayReasonFilter })}
          className="border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
        >
          {DELAY_REASON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {distinctCmos.length > 0 && (
          <select
            value={filters.cmoFilter}
            onChange={(e) => setFilters({ cmoFilter: e.target.value })}
            className="border border-gray-200 rounded px-2 py-1.5 text-sm max-w-[180px] truncate focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
          >
            <option value="all">All CMOs</option>
            {distinctCmos.map((cmo) => (
              <option key={cmo} value={cmo}>{cmo}</option>
            ))}
          </select>
        )}
        <select
          value={filters.productFilter}
          onChange={(e) => setFilters({ productFilter: e.target.value })}
          className="border border-gray-200 rounded px-2 py-1.5 text-sm max-w-[180px] truncate focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
        >
          <option value="all">All Products</option>
          {distinctProducts.map((product) => (
            <option key={product} value={product}>{product}</option>
          ))}
        </select>
        {isSupervisorOrAbove && (
          <select
            value={filters.userFilter}
            onChange={(e) => setFilters({ userFilter: e.target.value })}
            className="border border-gray-200 rounded px-2 py-1.5 text-sm max-w-[180px] truncate focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
          >
            <option value="all">All Users</option>
            {distinctUsers.map((email) => (
              <option key={email} value={email}>{email}</option>
            ))}
          </select>
        )}
        <div className="relative flex-1 min-w-[120px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search batch, product, lot, material..."
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
          />
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
          {filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Truncation Warning */}
      {isTruncated && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
          <span>Showing the most recent 500 sessions. Use filters to narrow results.</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          Failed to load dashboard data: {error.message}
          <button onClick={refresh} className="ml-2 underline">Retry</button>
        </div>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-green-600 mr-3" />
          <span className="text-gray-500">Loading sessions...</span>
        </div>
      )}

      {/* Session Table */}
      {!loading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ minWidth: isAdmin ? '1450px' : isSupervisorOrAbove ? '1100px' : '850px', width: '100%' }}>
              <colgroup>
                <col style={{ width: '130px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '130px' }} />
                {isSupervisorOrAbove && <col style={{ width: '90px' }} />}
                {isSupervisorOrAbove && <col style={{ width: '160px' }} />}
                {isSupervisorOrAbove && <col style={{ width: '150px' }} />}
                <col style={{ width: '150px' }} />
                {isAdmin && <col style={{ width: '80px' }} />}
                {isAdmin && <col style={{ width: '110px' }} />}
              </colgroup>
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-center">
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort('batchid')}
                    title="SAP inspection lot number identifying this batch"
                  >
                    Batch ID <SortIcon field="batchid" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort('batchtype')}
                    title="Whether the batch is a direct or indirect transport"
                  >
                    Batch Type <SortIcon field="batchtype" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort('productname')}
                    title="Product name associated with this batch"
                  >
                    Product <SortIcon field="productname" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort('status')}
                    title="Current status of the batch review (In Progress, Completed, etc.)"
                  >
                    Status <SortIcon field="status" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort('delayreason')}
                    title="Delay reason if the batch review is delayed"
                  >
                    Delay Info <SortIcon field="delayreason" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort('expectedreleasedate')}
                    title="Date the batch is expected to be released to market"
                  >
                    Expected Release <SortIcon field="expectedreleasedate" />
                  </th>
                  {isSupervisorOrAbove && (
                    <th
                      className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      onClick={() => handleSort('priority')}
                      title="Review priority level: High, Medium, or Low"
                    >
                      Priority <SortIcon field="priority" />
                    </th>
                  )}
                  {isSupervisorOrAbove && (
                    <th
                      className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      onClick={() => handleSort('useremail')}
                      title="The user who started this batch review"
                    >
                      Reviewer <SortIcon field="useremail" />
                    </th>
                  )}
                  {isSupervisorOrAbove && (
                    <th
                      className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                      onClick={() => handleSort('startedat')}
                      title="Date and time the batch review was started"
                    >
                      Started On <SortIcon field="startedat" />
                    </th>
                  )}
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort('completedat')}
                    title="Date and time the batch review was completed"
                  >
                    Completed On <SortIcon field="completedat" />
                  </th>
                  {isAdmin && (
                    <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap" title="Processing time from document completeness check to completion (falls back to session start for older reviews)">
                      Time
                    </th>
                  )}
                  {isAdmin && (
                    <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap" title="Time between BRR submission and SAP usage decision">
                      QP Release Time
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedSessions.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 12 : isSupervisorOrAbove ? 10 : 7} className="px-4 py-12 text-center text-gray-400">
                      No sessions found{filters.status !== 'all' || filters.search ? ' matching filters' : ''}.
                    </td>
                  </tr>
                ) : (
                  paginatedSessions.map((session) => {
                    const sessionId = session[columns.sessionid] || '';
                    const batchId = session[columns.batchid] || '';
                    const batchType = getBatchType(session, columns);
                    const rawPriority = getPriority(session, columns);
                    const priority = priorityOverrides[sessionId] || rawPriority;
                    const productName = getProductName(session, columns);
                    const status = session[columns.status];
                    const delayReasonVal = getDelayReason(session, columns);
                    const delayDetailsVal = getDelayDetails(session, columns);
                    const delayActionsVal = getDelayActions(session, columns);
                    const delayCmoNameVal = getDelayCmoName(session, columns);
                    const lotId = getInspectionLotId(session, columns);
                    const releaseInfo = lotId ? releaseDates[lotId] : null;
                    const delay = computeReleaseDelay(session[columns.submittedat], releaseInfo);
                    const isUpdating = priorityUpdating[sessionId];
                    return (
                      <tr
                        key={sessionId || Math.random()}
                        className="hover:bg-green-50 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-gray-900 text-center truncate">{batchId}</td>
                        <td className="px-4 py-3 text-gray-600 text-center whitespace-nowrap">
                          {batchType ? batchType.charAt(0).toUpperCase() + batchType.slice(1) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-center truncate" title={productName}>{productName || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <StatusBadge
                            status={status}
                            delayReason={delayReasonVal || null}
                            delayCmoName={delayCmoNameVal || null}
                            delayDetails={delayDetailsVal || null}
                            delayActions={delayActionsVal || null}
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-center whitespace-nowrap">
                          {delayReasonVal ? (DELAY_REASON_LABELS[delayReasonVal] || delayReasonVal) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-center whitespace-nowrap">
                          {formatDateShort(getExpectedReleaseDate(session, columns))}
                        </td>
                        {isSupervisorOrAbove && (
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {isAdmin && sessionId && batchId ? (
                              <select
                                value={priority || 'medium'}
                                onChange={(e) => handlePriorityChange(sessionId, batchId, e.target.value as Priority)}
                                disabled={isUpdating}
                                className={`text-xs font-medium rounded-full px-2 py-0.5 border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-green-400 ${
                                  priority === 'high' ? 'bg-red-100 text-red-800' :
                                  priority === 'low' ? 'bg-green-100 text-green-800' :
                                  'bg-yellow-100 text-yellow-800'
                                } ${isUpdating ? 'opacity-50' : ''}`}
                              >
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                              </select>
                            ) : priority === 'high' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                High
                              </span>
                            ) : priority === 'medium' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Medium
                              </span>
                            ) : priority === 'low' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Low
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                        {isSupervisorOrAbove && <td className="px-4 py-3 text-gray-600 text-center truncate" title={session[columns.useremail] || ''}>{session[columns.useremail] || '-'}</td>}
                        {isSupervisorOrAbove && <td className="px-4 py-3 text-gray-600 text-center whitespace-nowrap">{formatDate(session[columns.startedat])}</td>}
                        <td className="px-4 py-3 text-gray-600 text-center whitespace-nowrap">{formatDate(session[columns.completedat])}</td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-gray-600 text-center whitespace-nowrap">
                            {formatProcessingTime(session[columns.startedat], session[columns.completedat], getInProgressAt(session, columns), getDocCompletenessAt(session, columns))}
                          </td>
                        )}
                        {isAdmin && (
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {delay.status === 'pre-released' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Pre-released
                              </span>
                            ) : delay.status === 'rejected' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Rejected{delay.text ? ` (${delay.text})` : ''}
                              </span>
                            ) : delay.category && delay.hours !== null ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${DELAY_STYLES[delay.category]}`}>
                                  {formatReleaseDelay(delay.hours)}
                                </span>
                                {delay.text && <span className="text-[10px] text-gray-500">{delay.text}</span>}
                              </div>
                            ) : status === SessionStatus.Submitted || status === SessionStatus.Completed || status === SessionStatus.Rejected ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Pending
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Footer */}
          {sortedSessions.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
              <div className="flex items-center gap-2 text-gray-600 whitespace-nowrap">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
                >
                  {ROWS_PER_PAGE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <span>
                  {(safePage - 1) * rowsPerPage + 1}–{Math.min(safePage * rowsPerPage, sortedSessions.length)} of {sortedSessions.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <ChevronDown size={18} className="rotate-90" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <ChevronDown size={18} className="-rotate-90" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DashboardPage = React.memo(DashboardPageComponent);
